import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaEye,
  FaDownload,
  FaSpinner, 
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
  FaTimes,
  FaMicrophone
} from 'react-icons/fa';
import './Gallery.css';

// Axios instance
const isDevelopment = process.env.NODE_ENV === 'development';
const axiosInstance = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : '',
  timeout: 30000,
  withCredentials: true, // Important for sending cookies
  headers: {
    'Connection': 'keep-alive'
  }
});

function MobileCameraRoll({ hashFromAuth }) {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [downloadingMedia, setDownloadingMedia] = useState(null);
  const [weddingInfo, setWeddingInfo] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [mediaType, setMediaType] = useState('images'); // 'images', 'videos', 'audios'
  const [audioPlayingId, setAudioPlayingId] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const videoRef = React.useRef(null);
  const audioRef = React.useRef(null);
  const { hash: hashFromParams } = useParams();
  const hash = hashFromAuth || hashFromParams; // Use auth hash if available, fallback to URL
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Add iOS viewport height fix on component mount
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVh();
    window.addEventListener('resize', setVh);
    
    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  // Extract bride and groom names from wedding path
  const getBrideGroomNames = useCallback((weddingPath) => {
    if (!weddingPath) return '';
    
    // Helper function to split name by capital letters
    const splitByCapitals = (name) => {
      if (!name || name.length <= 1) return name;
      return name.replace(/([A-Z])/g, ' $1').trim();
    };
    
    // Wedding path format: "brideName-groomName"
    const parts = weddingPath.split('-');
    if (parts.length >= 2) {
      // Skip any numeric parts (for backwards compatibility with old format)
      const nameParts = parts.filter(part => isNaN(part) && part.length > 2);
      if (nameParts.length >= 2) {
        // Process bride name with capital letter splitting
        const brideRaw = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        const bride = splitByCapitals(brideRaw);
        
        // Process groom name with capital letter splitting
        const groomRaw = nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
        const groom = splitByCapitals(groomRaw);
        
        return `${bride} & ${groom}`;
      } else if (nameParts.length === 1) {
        const nameRaw = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        const name = splitByCapitals(nameRaw);
        return name;
      }
    }
    return weddingPath;
  }, []);

  const fetchWeddingInfo = useCallback(async () => {
    try {
      if (!hash) {
        setWeddingInfo(null);
        return;
      }

      const response = await axiosInstance.get(`/api/admin/wedding-info/${hash}`);
      setWeddingInfo(response.data.data);
    } catch (error) {
      console.error('Error fetching wedding info:', error);
      
      // If authentication required (401), redirect to login
      if (error.response && error.response.status === 401) {
        console.log('Authentication required, redirecting to login...');
        setLoading(false);
        navigate('/camera-roll-mobile/login', { replace: true });
        return;
      }
      
      // If wedding not found (404), redirect to home page
      if (error.response && error.response.status === 404) {
        console.log('Wedding not found, redirecting to home page...');
        setLoading(false);
        navigate('/', { replace: true });
        return;
      }
      
      setWeddingInfo(null);
    }
  }, [hash, navigate]);

  const fetchMedia = useCallback(async () => {
    try {
      if (!hash) {
        setMediaList([]);
        setLoading(false);
        return;
      }
      
      let endpoint = `/api/media/admin/${hash}/images`;
      
      // Use different endpoints based on media type
      if (mediaType === 'images') {
        endpoint = `/api/media/admin/${hash}/images`;
      } else if (mediaType === 'videos') {
        endpoint = `/api/media/admin/${hash}/videos`;
      } else if (mediaType === 'audios') {
        endpoint = `/api/media/admin/${hash}/audios`;
      }
      
      const response = await axiosInstance.get(endpoint);
      setMediaList(response.data.data);
      setCurrentMediaIndex(0); // Reset to first item when changing media type
      setLoading(false);
    } catch (err) {
      console.error("Error fetching media:", err);
      
      // If authentication required (401), redirect to login
      if (err.response && err.response.status === 401) {
        console.log('Authentication required, redirecting to login...');
        setLoading(false);
        navigate('/camera-roll-mobile/login', { replace: true });
        return;
      }
      
      if (err.response && err.response.status === 404) {
        console.log('Media endpoint returned 404, redirecting to home page...');
        setLoading(false);
        navigate('/', { replace: true });
        return;
      }
      
      setError(t('admin.error'));
      setLoading(false);
    }
  }, [hash, t, navigate, mediaType]);

  useEffect(() => {
    fetchWeddingInfo();
    fetchMedia();
  }, [fetchWeddingInfo, fetchMedia]);



  const handleDownload = async (id) => {
    try {
      setDownloadingMedia(id);
      const response = await axiosInstance.get(`/api/media/download/${id}`, {
        responseType: 'blob'
      });
      
      // Get the content type from response headers
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Create a blob URL with the correct MIME type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'download';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      } else {
        // Use the file_name from media object if available
        const mediaItem = mediaList.find(m => m.id === id);
        if (mediaItem && mediaItem.file_name) {
          filename = mediaItem.file_name;
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadingMedia(null);
    } catch (err) {
      console.error("Error downloading media:", err);
      setError(t('admin.downloadError'));
      setDownloadingMedia(null);
    }
  };

  const goToNextMedia = () => {
    if (currentMediaIndex < mediaList.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
    }
  };

  const goToPrevMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex(currentMediaIndex - 1);
    }
  };

  const goToMedia = (index) => {
    if (index >= 0 && index < mediaList.length) {
      setCurrentMediaIndex(index);
    }
  };
  
  // Handle media type change
  const handleMediaTypeChange = (type) => {
    if (type !== mediaType) {
      setLoading(true);
      setMediaType(type);
    }
  };
  
  // Video player control functions
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (videoRef.current.paused) {
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch(err => {
        console.error("Error playing video:", err);
        setIsVideoPlaying(false);
      });
    } else {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  };
  
  const toggleMute = () => {
    if (!videoRef.current) return;
    
    const newMutedState = !isMuted;
    videoRef.current.muted = newMutedState;
    setIsMuted(newMutedState);
    
    // Save previous volume if muting
    if (newMutedState) {
      videoRef.current.volume = 0;
    } else {
      videoRef.current.volume = volume || 0.5;
    }
  };
  
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (!videoRef.current) return;
    
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
    
    // Update muted state
    if (newVolume === 0) {
      setIsMuted(true);
      videoRef.current.muted = true;
    } else if (isMuted) {
      setIsMuted(false);
      videoRef.current.muted = false;
    }
  };
  
  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    
    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    
    setCurrentTime(videoRef.current.currentTime);
    setDuration(videoRef.current.duration);
  };
  
  const handleSeek = (e) => {
    if (!videoRef.current) return;
    
    const seekTime = parseFloat(e.target.value);
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };
  
  // Format time in MM:SS format
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "00:00";
    
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Reset video states when changing media
  useEffect(() => {
    setIsVideoPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsVideoLoading(true);
    setVideoError(null);
    // Don't reset volume settings between videos
  }, [currentMediaIndex]);

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mb-4"></div>
          <h2 className="text-xl font-semibold">{t('admin.loading')}</h2>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block rounded-full h-12 w-12 bg-red-100 text-red-500 flex items-center justify-center mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-red-500">{error}</h2>
        </div>
      </div>
    );
  }

  // Render no hash state
  if (!hash) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{t('admin.noHashMessage')}</h2>
          <p className="text-gray-600">{t('admin.noHashExample')}</p>
        </div>
      </div>
    );
  }

  // Render empty state
  if (mediaList.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{t('admin.title')}</h2>
          {mediaType === 'images' ? (
            <p className="text-gray-600">{t('admin.emptyState.noPhotos')}</p>
          ) : mediaType === 'videos' ? (
            <p className="text-gray-600">{t('admin.emptyState.noVideos')}</p>
          ) : mediaType === 'audios' ? (
            <p className="text-gray-600">{t('admin.emptyState.noVoiceNotes')}</p>
          ) : (
            <p className="text-gray-600">{t('admin.emptyState.noPhotos')}</p>
          )}
          
          {/* Media Type Toggle Buttons - Minimized */}
          <div className="flex justify-center mt-4">
            <div className="flex gap-1 items-center">
              <button
                className={`text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                  mediaType === 'images' 
                    ? 'bg-pink-400 text-white font-medium shadow-sm' 
                    : 'bg-gray-100 text-gray-500'
                }`}
                onClick={() => handleMediaTypeChange('images')}
              >
                {t('admin.tabs.photos')}
              </button>
              <div className="w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
              <button
                className={`text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                  mediaType === 'videos' 
                    ? 'bg-pink-400 text-white font-medium shadow-sm' 
                    : 'bg-gray-100 text-gray-500'
                }`}
                onClick={() => handleMediaTypeChange('videos')}
              >
                {t('admin.tabs.videos')}
              </button>
              <div className="w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
              <button
                className={`text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                  mediaType === 'audios' 
                    ? 'bg-pink-400 text-white font-medium shadow-sm' 
                    : 'bg-gray-100 text-gray-500'
                }`}
                onClick={() => handleMediaTypeChange('audios')}
              >
                {t('admin.tabs.voiceNotes')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentMedia = mediaList[currentMediaIndex];

  return (
    <div className="mobile-camera-roll">
      {/* Header */}
      <header className="bg-white p-3 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center flex-1 min-w-0">
            <img 
              src="/new-logo.png" 
              alt="Weddingram Logo" 
              className="h-7 w-auto drop-shadow-lg mr-2 flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              {weddingInfo && (
                <h1 className="text-lg font-bold text-pink-500 truncate">
                  {getBrideGroomNames(weddingInfo.weddingPath)}
                </h1>
              )}
              <p className="text-xs text-gray-600">
                Digital Album
              </p>
            </div>
          </div>
          
          {/* Media Type Toggle Buttons - Top Right - Responsive */}
          <div className="flex gap-1 items-center flex-shrink-0 ml-2">
            <button
              className={`text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                mediaType === 'images' 
                  ? 'bg-pink-400 text-white font-medium shadow-sm' 
                  : 'bg-gray-100 text-gray-500'
              }`}
              onClick={() => handleMediaTypeChange('images')}
            >
              {t('admin.tabs.photos')}
            </button>
            <div className="w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
            <button
              className={`text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                mediaType === 'videos' 
                  ? 'bg-pink-400 text-white font-medium shadow-sm' 
                  : 'bg-gray-100 text-gray-500'
              }`}
              onClick={() => handleMediaTypeChange('videos')}
            >
              {t('admin.tabs.videos')}
            </button>
            <div className="w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
            <button
              className={`text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                mediaType === 'audios' 
                  ? 'bg-pink-400 text-white font-medium shadow-sm' 
                  : 'bg-gray-100 text-gray-500'
              }`}
              onClick={() => handleMediaTypeChange('audios')}
            >
              {t('admin.tabs.voiceNotes')}
            </button>
          </div>
        </div>
        
        {/* Warning Message for Videos - Minimized */}
        {mediaType === 'videos' && (
          <div className="mt-1 px-2 py-1 bg-yellow-50 border-t border-b border-yellow-100">
            <p className="text-[10px] text-yellow-700 text-center leading-tight">
              Videoların kalitesi yüksek olduğundan ötürü telefonunuzda yüklenmesi uzun sürebilir.
            </p>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow flex flex-col relative overflow-hidden">
        {/* Image/Video/Audio Display */}
        <div className="mobile-media-container flex-shrink-0 relative">
          {currentMedia.file_type.startsWith('image/') ? (
            <img
              src={currentMedia.storage_url}
              alt={currentMedia.file_name}
              className="mobile-media-content"
            />
          ) : currentMedia.file_type.startsWith('audio/') ? (
            /* Audio Player */
            <div className="audio-player-container flex flex-col items-center justify-center h-full min-h-[300px] bg-gradient-to-br from-pink-50 via-white to-pink-100 rounded-2xl mx-4 my-4 p-6 shadow-lg">
              {/* Microphone Icon with Pulse Animation */}
              <div className={`relative mb-6 ${audioPlayingId === currentMedia.id ? 'animate-pulse' : ''}`}>
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-300 to-pink-400 flex items-center justify-center shadow-lg">
                  <FaMicrophone className="text-white text-4xl" />
                </div>
                {audioPlayingId === currentMedia.id && (
                  <div className="absolute inset-0 rounded-full border-4 border-pink-300 animate-ping opacity-50"></div>
                )}
              </div>
              
              {/* Sender Name (from note field) */}
              {currentMedia.note && (
                <div className="mb-4 text-center">
                  <p className="text-pink-500 font-semibold text-lg">{currentMedia.note.split('|')[0] || currentMedia.note}</p>
                  <p className="text-gray-400 text-xs mt-1">{t('admin.voiceNote')}</p>
                </div>
              )}
              
              {/* Audio Element (hidden) */}
              <audio
                ref={audioRef}
                src={currentMedia.storage_url}
                preload="metadata"
                onTimeUpdate={(e) => setAudioCurrentTime(e.target.currentTime)}
                onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
                onPlay={() => setAudioPlayingId(currentMedia.id)}
                onPause={() => setAudioPlayingId(null)}
                onEnded={() => {
                  setAudioPlayingId(null);
                  setAudioCurrentTime(0);
                }}
              />
              
              {/* Progress Bar */}
              <div className="w-full max-w-xs mb-4">
                <div className="relative h-2 bg-pink-100 rounded-full overflow-hidden">
                  <div 
                    className="absolute h-full bg-gradient-to-r from-rose-300 to-pink-400 rounded-full transition-all duration-100"
                    style={{ width: `${(audioCurrentTime / (audioDuration || 1)) * 100}%` }}
                  ></div>
                </div>
                <input 
                  type="range"
                  className="absolute w-full max-w-xs opacity-0 cursor-pointer h-2"
                  min="0"
                  max={audioDuration || 100}
                  value={audioCurrentTime}
                  onChange={(e) => {
                    const time = parseFloat(e.target.value);
                    if (audioRef.current) {
                      audioRef.current.currentTime = time;
                      setAudioCurrentTime(time);
                    }
                  }}
                  style={{ top: 'auto', marginTop: '-8px' }}
                />
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{formatTime(audioCurrentTime)}</span>
                  <span>{formatTime(audioDuration)}</span>
                </div>
              </div>
              
              {/* Play/Pause Button */}
              <button
                onClick={() => {
                  if (audioRef.current) {
                    if (audioPlayingId === currentMedia.id) {
                      audioRef.current.pause();
                    } else {
                      audioRef.current.play();
                    }
                  }
                }}
                className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-300 to-pink-400 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
              >
                {audioPlayingId === currentMedia.id ? (
                  <FaPause className="text-white text-xl ml-0" />
                ) : (
                  <FaPlay className="text-white text-xl ml-1" />
                )}
              </button>
            </div>
          ) : (
            <div className="video-player-container">
              <video
                key={currentMedia.id}
                ref={videoRef}
                src={currentMedia.storage_url}
                className={`mobile-media-content ${isVideoLoading ? 'loading' : ''}`}
                playsInline
                preload="metadata"
                autoPlay={false}
                onLoadStart={() => setIsVideoLoading(true)}
                onError={(e) => {
                  console.error("Video error:", e);
                  setVideoError("Failed to load video");
                  setIsVideoLoading(false);
                }}
                onLoadedMetadata={(e) => {
                  // Make sure video is ready to play
                  if (e.target.readyState >= 2) {
                    e.target.poster = ''; // Remove any default poster
                    setDuration(e.target.duration);
                  }
                }}
                onLoadedData={() => {
                  setIsVideoLoading(false);
                  setVideoError(null);
                }}
                onCanPlay={() => {
                  setIsVideoLoading(false);
                  setVideoError(null);
                }}
                onTimeUpdate={handleTimeUpdate}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onEnded={() => setIsVideoPlaying(false)}
                onClick={togglePlay}
              />
              
              {/* Loading Indicator */}
              {isVideoLoading && (
                <div className="video-loading-overlay">
                  <div className="video-loading-spinner"></div>
                </div>
              )}
              
              {/* Error Message */}
              {videoError && (
                <div className="video-error-overlay">
                  <div className="video-error-message">
                    <FaTimes className="mr-2" /> {videoError}
                  </div>
                </div>
              )}
              
              {/* Large Play Button Overlay */}
              <div 
                className={`video-play-overlay ${isVideoPlaying ? 'playing' : ''} ${!isVideoLoading ? 'visible' : ''}`}
                onClick={togglePlay}
              >
                {!isVideoPlaying && !isVideoLoading && (
                  <svg className="video-play-button" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="12" fill="white" fillOpacity="0.5" />
                    <circle cx="12" cy="12" r="10" fill="white" />
                    <path d="M16 12L10 16V8L16 12Z" fill="#1F2937" />
                  </svg>
                )}
              </div>
              
              {/* Custom Video Controls */}
              <div className="video-controls">
                {/* Progress Bar */}
                <div className="video-progress-container">
                  <input 
                    type="range"
                    className="video-progress"
                    min="0"
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSeek}
                  />
                  <div 
                    className="video-progress-filled"
                    style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
                  ></div>
                </div>
                
                {/* Control Buttons */}
                <div className="video-controls-buttons">
                  {/* Play/Pause Button */}
                  <button 
                    className="video-control-button"
                    onClick={togglePlay}
                    aria-label={isVideoPlaying ? "Pause" : "Play"}
                  >
                    {isVideoPlaying ? <FaPause size={16} /> : <FaPlay size={16} />}
                  </button>
                  
                  {/* Time Display */}
                  <div className="video-time">
                    <span>{formatTime(currentTime)}</span>
                    <span className="video-time-separator">/</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  
                  {/* Volume Controls */}
                  <div className="video-volume-container">
                    <button 
                      className="video-control-button"
                      onClick={toggleMute}
                      aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted || volume === 0 ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
                    </button>
                    <input 
                      type="range"
                      className="video-volume-slider"
                      min="0"
                      max="1"
                      step="0.05"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                    />
                  </div>
                  
                  {/* Fullscreen Button */}
                  <button 
                    className="video-control-button"
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  >
                    {isFullscreen ? <FaCompress size={16} /> : <FaExpand size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation Arrows - Enhanced */}
          <button
            onClick={goToPrevMedia}
            disabled={currentMediaIndex === 0}
            className={`mobile-nav-arrow mobile-nav-prev ${
              currentMediaIndex === 0 ? 'opacity-30' : 'opacity-70 active:opacity-100'
            }`}
            aria-label="Previous image"
          >
            <FaChevronLeft size={20} />
          </button>
          
          <button
            onClick={goToNextMedia}
            disabled={currentMediaIndex === mediaList.length - 1}
            className={`mobile-nav-arrow mobile-nav-next ${
              currentMediaIndex === mediaList.length - 1 ? 'opacity-30' : 'opacity-70 active:opacity-100'
            }`}
            aria-label="Next image"
          >
            <FaChevronRight size={20} />
          </button>

          {/* Action Buttons - Minimized */}
          <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
            {/* View button - hide for audio files */}
            {!currentMedia.file_type.startsWith('audio/') && (
              <button
                onClick={() => window.open(currentMedia.storage_url, '_blank')}
                className="w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm shadow-md flex items-center justify-center text-gray-600 hover:text-blue-500 active:text-blue-600 transition-colors"
                title={t('admin.view')}
                aria-label="View full size"
              >
                <FaEye size={14} />
              </button>
            )}
            
            <button
              onClick={() => handleDownload(currentMedia.id)}
              disabled={downloadingMedia === currentMedia.id}
              className={`w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm shadow-md flex items-center justify-center text-gray-600 hover:text-green-500 active:text-green-600 transition-colors ${
                downloadingMedia === currentMedia.id ? 'opacity-50' : ''
              }`}
              title={t('admin.download')}
              aria-label="Download media"
            >
              {downloadingMedia === currentMedia.id ? (
                <FaSpinner className="ultra-spinner" size={14} />
              ) : (
                <FaDownload size={14} />
              )}
            </button>
          </div>

          {/* Note Display (if exists) - Overlay - Not for audio files */}
          {currentMedia.note && !currentMedia.file_type.startsWith('audio/') && (
            <div className="absolute bottom-4 left-4 right-20 z-10 bg-white/60 backdrop-blur-sm rounded-lg p-2 shadow-md">
              {(() => {
                const parts = currentMedia.note.split('|');
                const hasNameAndNote = parts.length === 2 && parts[0].trim() && parts[1].trim();
                const nameOnly = parts.length === 1 || (parts.length === 2 && parts[0].trim() && !parts[1].trim());
                const noteOnly = parts.length === 2 && !parts[0].trim() && parts[1].trim();
                
                if (hasNameAndNote) {
                  return (
                    <div className="space-y-1">
                      <p className="text-gray-700 leading-relaxed text-sm">{parts[1].trim()}</p>
                      <p className="text-right text-xs text-pink-500 font-medium">— {parts[0].trim()}</p>
                    </div>
                  );
                } else if (nameOnly) {
                  return (
                    <div className="flex items-center justify-center">
                      <p className="text-gray-700 font-medium text-sm">{parts[0].trim()}</p>
                    </div>
                  );
                } else {
                  const displayText = noteOnly ? parts[1].trim() : currentMedia.note;
                  return (
                    <p className="text-gray-700 leading-relaxed text-sm">{displayText}</p>
                  );
                }
              })()}
            </div>
          )}
        </div>

        {/* Thumbnail Navigation */}
        <div className="mobile-thumbnails mt-4">
          {mediaList.map((media, index) => (
            <div
              key={media.id}
              className={`mobile-thumbnail ${
                index === currentMediaIndex ? 'active' : ''
              }`}
              onClick={() => goToMedia(index)}
            >
              {media.file_type.startsWith('image/') ? (
                <img
                  src={media.storage_url}
                  alt={`Thumbnail ${index + 1}`}
                  loading="lazy"
                />
              ) : media.file_type.startsWith('audio/') ? (
                /* Audio Thumbnail */
                <div className="audio-thumbnail-container w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-pink-100 to-pink-200 rounded-lg">
                  <FaMicrophone className="text-pink-400 text-lg mb-1" />
                  {media.note && (
                    <p className="text-[8px] text-pink-500 font-medium truncate max-w-full px-1">
                      {media.note.split('|')[0]?.substring(0, 8) || ''}
                    </p>
                  )}
                </div>
              ) : (
                <div className="video-thumbnail-container">
                  <video
                    src={media.storage_url}
                    className="video-thumbnail"
                    preload="metadata"
                    muted
                    onLoadedData={(e) => {
                      // Set video to first frame for thumbnail
                      e.target.currentTime = 0.1;
                    }}
                  />
                  <div className="video-indicator">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="white" fillOpacity="0.3" />
                      <circle cx="12" cy="12" r="8" fill="white" />
                      <path d="M15.5 12L10 15.5V8.5L15.5 12Z" fill="#1F2937" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default MobileCameraRoll;
